import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import authService from '../services/auth';
import { ensureApiAuthenticated, getFrontendAuthMode, type FrontendAuthMode } from '../services/api';
import { getSessionUser, isSessionValid, clearSessionToken } from '../services/api';
import { signInWithEntra, handleEntraRedirect, hasPendingEntraRedirectResponse } from '../services/entraAuth';

interface AuthContextType {
  isAuthenticated: boolean;
  user: { userId: string; username: string } | null;
  authMode: FrontendAuthMode;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  loginWithEntra: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<{ userId: string; username: string } | null>(null);
  const [authMode] = useState<FrontendAuthMode>(getFrontendAuthMode());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from session storage and auto-login with service account
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (authMode === 'entra' || authMode === 'hybrid') {
          const currentUser = authService.getCurrentUser();
          const sessionUser = getSessionUser();
          const validSession = isSessionValid();

          // Restore valid session first when there's no redirect payload to process.
          if (authMode === 'entra') {
            if (currentUser && validSession && sessionUser?.authProvider === 'entra') {
              await ensureApiAuthenticated();
              const recoveredUser = authService.getCurrentUser();
              if (recoveredUser) {
                setUser({ userId: recoveredUser.userId, username: recoveredUser.username });
                setIsAuthenticated(true);
                return;
              }
            }
            if (currentUser && sessionUser?.authProvider !== 'entra') {
              clearSessionToken();
            }
          } else if (currentUser && validSession) {
            if (sessionUser?.authProvider === 'entra') {
              await ensureApiAuthenticated();
              const recoveredUser = authService.getCurrentUser();
              if (recoveredUser) {
                setUser({ userId: recoveredUser.userId, username: recoveredUser.username });
                setIsAuthenticated(true);
                return;
              }
            } else {
              setUser({ userId: currentUser.userId, username: currentUser.username });
              setIsAuthenticated(true);
              return;
            }
          }

          // Process redirect result when Entra sent us back with auth response params.
          if (hasPendingEntraRedirectResponse()) {
            const handled = await handleEntraRedirect();
            if (handled) {
              const entraUser = authService.getCurrentUser();
              if (entraUser) {
                setUser({ userId: entraUser.userId, username: entraUser.username });
                setIsAuthenticated(true);
                return;
              }
            }
          }
        } else if (authMode === 'local') {
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            setUser({ userId: currentUser.userId, username: currentUser.username });
            setIsAuthenticated(true);
            return;
          }

          // local mode only: auto-login with service account credentials
          await ensureApiAuthenticated();

          const serviceUser = authService.getCurrentUser();
          if (serviceUser) {
            setUser({ userId: serviceUser.userId, username: serviceUser.username });
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to initialize auth';
        setError(message);
        console.error('Failed to initialize auth:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [authMode]);

  const login = useCallback(async (username: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      await authService.login({ username, password });
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        setUser({ userId: currentUser.userId, username: currentUser.username });
        setIsAuthenticated(true);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loginWithEntra = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // signInWithEntra() calls loginRedirect — browser navigates away.
      // Auth state is set on return via handleEntraRedirect() in initializeAuth.
      await signInWithEntra();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Entra login failed';
      setError(message);
      setLoading(false);
      throw err;
    }
    // Do NOT call setLoading(false) here — page is navigating away.
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, authMode, loading, error, login, loginWithEntra, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
